import { Box, Button, CircularProgress, Snackbar, TablePagination, Typography, makeStyles } from '@material-ui/core'
import { ChevronLeft, DoneAll } from '@material-ui/icons'
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { ALL_COMPOSITIONS, ALL_ENTITY_TYPES, MessageCompositionFilter, TagReviewHeader, TagReviewSortField } from './TagReviewHeader'
import { TagReviewEntityType, TagReviewItem, TagReviewStatus } from './types'

import React from 'react'
import { useParams } from 'react-router'
import { HISTORY } from '../router'
import { TagReviewCard } from './TagReviewCard'
import { useListTagReviewBatches } from './hooks/useListTagReviewBatches'
import { useListTagReviewItems } from './hooks/useListTagReviewItems'
import { useApproveAllPendingTagReviewItems } from './hooks/useTagReviewMutations'

type StatusFilter = TagReviewStatus | 'all'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100%'
  },
  breadcrumb: {
    maxWidth: 960,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1)
  },
  backButton: {
    textTransform: 'none' as const,
    color: theme.palette.text.secondary
  },
  feed: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    maxWidth: 960,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  filterBar: {
    display: 'flex',
    justifyContent: 'flex-start'
  },
  pagination: {
    maxWidth: 960,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingBottom: theme.spacing(4)
  }
}))

export const BatchDetail: React.FC = () => {
  const classes = useStyles()
  const { batchId } = useParams<{ batchId: string }>()
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('pending')
  const [filterEntityTypes, setFilterEntityTypes] = React.useState<TagReviewEntityType[]>(ALL_ENTITY_TYPES)
  const [filterCompositions, setFilterCompositions] = React.useState<MessageCompositionFilter[]>(ALL_COMPOSITIONS)
  const [sortField, setSortField] = React.useState<TagReviewSortField>('popularity')
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc')
  const [itemStatuses, setItemStatuses] = React.useState<Record<string, TagReviewStatus>>({})

  const { data: batchesData } = useListTagReviewBatches(undefined, { fetchPolicy: 'cache-first' })
  const { data: itemsData, loading: itemsLoading } = useListTagReviewItems({ input: { batchId } })
  const [approveAllPending, { loading: approveAllLoading }] = useApproveAllPendingTagReviewItems()

  const batch = batchesData?.listTagReviewBatches?.batches?.find(b => b.id === batchId)
  const batchItems = React.useMemo(() => itemsData?.listTagReviewItems?.items ?? [], [itemsData]) as TagReviewItem[]

  const [toast, setToast] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null)
  const lastToast = React.useRef(toast)
  if (toast) lastToast.current = toast

  const handleStatusChange = (id: string, newStatus: TagReviewStatus) => {
    setItemStatuses(prev => ({ ...prev, [id]: newStatus }))
    if (newStatus === 'queued') setToast({ message: 'Approved & queued', severity: 'success' })
    else if (newStatus === 'rejected') setToast({ message: 'Item rejected and feedback captured', severity: 'success' })
    else if (newStatus === 'pending') setToast({ message: 'Reverted to pending', severity: 'success' })
  }

  const handleSortChange = (field: TagReviewSortField, dir: 'asc' | 'desc') => {
    setSortField(field)
    setSortDir(dir)
    setPage(0)
  }

  const statusCounts = React.useMemo(() => {
    const filtered = batchItems
      .filter(item => filterEntityTypes.includes(item.entityType))
      .filter(item => item.entityType === 'MarketplaceListing' || filterCompositions.includes((item.messageComposition ?? 'Unknown') as MessageCompositionFilter))
    const counts: Record<string, number> = { all: filtered.length }
    for (const item of filtered) {
      const s = itemStatuses[item.id] ?? item.status
      counts[s] = (counts[s] || 0) + 1
    }
    return counts
  }, [batchItems, itemStatuses, filterEntityTypes, filterCompositions])

  const sortValue = (item: TagReviewItem): number => {
    if (sortField === 'popularity') return item.popularityScore ?? 0
    if (sortField === 'queuedAt') return item.queuedAt ?? 0
    return item.createdAt
  }

  const filteredItems = React.useMemo(() => {
    let result = batchItems
      .filter(item => filterEntityTypes.includes(item.entityType))
      .filter(item => item.entityType === 'MarketplaceListing' || filterCompositions.includes((item.messageComposition ?? 'Unknown') as MessageCompositionFilter))
      .filter(item => statusFilter === 'all' || (itemStatuses[item.id] ?? item.status) === statusFilter)
    result = [...result].sort((a, b) =>
      sortDir === 'desc' ? sortValue(b) - sortValue(a) : sortValue(a) - sortValue(b)
    )
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchItems, statusFilter, filterEntityTypes, filterCompositions, sortField, sortDir, itemStatuses])

  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newValue: StatusFilter | null) => {
    if (newValue !== null) {
      setStatusFilter(newValue)
      setPage(0)
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.breadcrumb}>
        <Button className={classes.backButton} startIcon={<ChevronLeft />} onClick={() => HISTORY.push('/tag-review')}>
          Back to all batches
        </Button>
      </Box>
      <TagReviewHeader
        title={batch?.title ?? '…'}
        description={batch?.description}
        filterEntityTypes={filterEntityTypes}
        onFilterEntityTypesChange={types => {
          setFilterEntityTypes(types)
          setPage(0)
        }}
        filterCompositions={filterCompositions}
        onFilterCompositionsChange={compositions => {
          setFilterCompositions(compositions)
          setPage(0)
        }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={handleSortChange}
      />
      <Box className={classes.feed}>
        <Box className={classes.filterBar} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <ToggleButtonGroup value={statusFilter} exclusive onChange={handleFilterChange} size='small'>
            <ToggleButton value='pending'>Pending ({statusCounts.pending || 0})</ToggleButton>
            <ToggleButton value='queued'>Queued ({statusCounts.queued || 0})</ToggleButton>
            <ToggleButton value='completed'>Completed ({statusCounts.completed || 0})</ToggleButton>
            <ToggleButton value='rejected'>Rejected ({statusCounts.rejected || 0})</ToggleButton>
            <ToggleButton value='error'>Error ({statusCounts.error || 0})</ToggleButton>
          </ToggleButtonGroup>
          {statusFilter === 'pending' && (
            <Button
              variant='contained'
              color='primary'
              disableElevation
              size='small'
              startIcon={<DoneAll />}
              disabled={(statusCounts.pending || 0) === 0 || approveAllLoading}
              onClick={async () => {
                const pending = paginatedItems.filter(item => (itemStatuses[item.id] ?? item.status) === 'pending')
                if (pending.length === 0) return
                try {
                  const res = await approveAllPending({
                    variables: { input: { batchId, itemIds: pending.map(item => item.id) } }
                  })
                  if (res.errors?.length) {
                    setToast({ message: res.errors[0].message || 'Failed to approve items', severity: 'error' })
                    return
                  }
                  const approved: string[] = (res.data?.approveAllPendingTagReviewItemsV2 ?? []).map((i: any) => i.id)
                  const failed = pending.length - approved.length
                  approved.forEach(id => setItemStatuses(prev => ({ ...prev, [id]: 'queued' })))
                  if (failed > 0) {
                    setToast({ message: `${failed} item${failed > 1 ? 's' : ''} failed to approve`, severity: 'error' })
                  } else {
                    setToast({
                      message: `${approved.length} item${approved.length > 1 ? 's' : ''} approved & queued`,
                      severity: 'success'
                    })
                  }
                } catch (err: any) {
                  setToast({ message: err?.message || 'Failed to approve items', severity: 'error' })
                }
              }}>
              Approve All Pending on Page
            </Button>
          )}
        </Box>
        {statusFilter === 'queued' && (
          <Alert severity='info'>
            Queued items are picked up by an hourly job that processes the oldest items in the queue, across batches.
          </Alert>
        )}
        {itemsLoading ? (
          <Box py={8} textAlign='center'>
            <CircularProgress size={28} />
          </Box>
        ) : paginatedItems.length === 0 ? (
          <Box py={8} textAlign='center'>
            <Typography variant='body1' color='textSecondary'>
              No items match the current filter.
            </Typography>
          </Box>
        ) : (
          paginatedItems.map(item => (
            <TagReviewCard
              key={item.id}
              item={item}
              status={itemStatuses[item.id] ?? item.status}
              onStatusChange={handleStatusChange}
              onError={msg => setToast({ message: msg, severity: 'error' })}
            />
          ))
        )}
      </Box>
      <Box className={classes.pagination}>
        <TablePagination
          component='div'
          count={filteredItems.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={lastToast.current?.severity} onClose={() => setToast(null)} variant='filled'>
          {lastToast.current?.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
